/** This service provides interaction with user preferences and user profiles.
The format for the userPreferences.json is:
{
	"userPreferences": {
		"map": {
			"latitude": "40.179397",
			"longitude": "-104.991531",
			"zoom": "8",
			"baseMap": "GoogleStreet"
		}
	}
}

Each time a new preferences is added, consideration should be given to how that preference should be implemented.
The following is a place to start:
	config-preferences.component.html: add html to display the preference
	config-preferences.component.ts: onSubmit should save the preference, ngOnInit may need adjustment if preference
	should have a pulldown selection
	map.component.ts: map.on(i.e. moveend & baselayerchange) should save the preference

Obviously, the effected files will be determined by the type of user preference.

This service directly accesses the user web services rather than utilizing the users.service.

Unfortunately, this class was created before the app-config service concept was introduced.  Some of the
functionality in this class should be moved to the app-config service.
*/

import {throwError as observableThrowError,  Observable, of, Subject, timer, BehaviorSubject } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpHeaders, HttpClient, HttpResponse } from '@angular/common/http';
import { UsersService } from './users.service';
import { AppConfigService } from './app-config.service';
import { CookieService } from 'ngx-cookie';
import { MapService } from './map.service';
import { AuthService } from './auth.service';
import { Router } from '@angular/router';
import { Location } from '@angular/common';

// declare variable defined elsewhere but used here
declare var $;
declare var defaultSettings: any;

// This class will control configuration settings.


@Injectable()
export class ConfigService {

	appReference: any;
	userName: string;
	userPassword: string;
	currentUser;  // actual user from user web services
	userProfile: any;
	userPreferences: any = {};
	usersService: UsersService;
	public systemConfiguration: any = {};
	public systemConfigurationAfterProcessing: any = {};
	baseDataDir: string;
	baseDir: string;
	realUser = false;  // this is going to determine whether user preferences are saved/utilized
	isGuestAllowed = null;
	preferencesBehaviorSubject = new BehaviorSubject<any>(defaultSettings);
	preferencesObservable;

	// provide an end date during testing - this overrides any other date setting
	useDeveloperDate = false;
	developerDate: any = null;

	includeDecorationSubject = new BehaviorSubject<boolean>(true);
	includeDecorationObservable;

	constructor ( private http: HttpClient, 
		private appConfigService: AppConfigService, 
		private authService: AuthService, 
		private router: Router,
		private location: Location,
		private _cookieService: CookieService,
		usersService: UsersService ) {
		this.usersService = usersService;
		this.verifyBrowser();
		this.getAppConfigInfo ();
		this.readSystemConfiguration();
		this.applyConfigFilePreferences();
		this.preferencesBehaviorSubject.next( this.userPreferences);
		this.preferencesObservable = this.preferencesBehaviorSubject.asObservable();
		this.includeDecorationObservable = this.includeDecorationSubject.asObservable();

		/*
		this.preferencesObservable.subscribe({ 
			next: (m) => {
				console.log("Got a new value here!!!");
			}
		});
		*/
	};

	getPreferencesObservable() {
		return this.preferencesObservable;
	}

	// saveAppReference allow a hook back into the app.component so we can set everything up properly based on login
	saveAppReference ( app: any ) {
		this.appReference = app;
	}

	getAppReference ( ) {
		return this.appReference;
	}

	isARealUser() {
		return this.realUser;
	}

	getAppConfigInfo() {
		this.userName = this.appConfigService.getUserName();
		this.userPassword = this.appConfigService.getUserPassword();
		console.log('Setting app config info', this.userName);
	}

	checkIfGuestAllowed() {
		return this.appConfigService.getIfGuestIsAllowed();
	}

	getConfigFilePreferences () {
		return this.systemConfigurationAfterProcessing;
	}

	getUserPreferences () {
		return this.userPreferences;
	}

	// This routine reads the configuration file
	readSystemConfiguration () {
		this.systemConfiguration = this.appConfigService.getConfig();
		if ( this.systemConfiguration != null ) {
			this.baseDataDir = this.appConfigService.getBaseDataDir();
			this.baseDir = this.appConfigService.getBaseDir();
			console.log(this.baseDataDir, this.baseDir );
		} else {
			alert('Unable to read configuration file.');
		}
	}

	// Given userPreferences as a base set of preferences, augment with addnlPreferences.
	// This is useful to supplement with both configuration file preferences
	// and user web services preferences.
	// userPreferences and addnlPreferences are in the forat { "map" : { "latitude": 39.742043, "longitude": -104.991532, "zoom": 9 }}
	applySpecificPreferences(addnlPreferences) {
		// console.log(this.userPreferences);
		// console.log(addnlPreferences);
		console.log("In applySpecificPreferences");
		if ( addnlPreferences == undefined ) {
			console.log("Warning - preferences are undefined.")
			return;
		}
		// merge userPreferences and addnlPreferences
		this.userPreferences.preferences = {
			...this.userPreferences.preferences,
			...addnlPreferences
		};
		this.preferencesBehaviorSubject.next( 
			this.userPreferences );

		// console.log( this.userPreferences.preferences );

	}

	// Using the userPreferences parameter passed in as a base
	// starting point, supplement those preferences
	// with the configuration file.
	applyConfigFilePreferences() {
		// set up defaults without defaultBaseMaps
		this.userPreferences.preferences = 
			JSON.parse(JSON.stringify( defaultSettings ));
		delete this.userPreferences.preferences.map.defaultBaseMaps;

		if ( this.systemConfiguration.DemoMode ) {
			this.applySpecificPreferences(this.systemConfiguration.OperatorInterfaceDemoMode.defaultSettings);
		} else {
			this.applySpecificPreferences(this.systemConfiguration.OperatorInterface.defaultSettings);
		}

		this.systemConfigurationAfterProcessing = 
			JSON.parse(JSON.stringify(this.userPreferences.preferences));

		this.developerDate = this.systemConfiguration.OperatorInterfaceDemoMode.periodEndDate;
		this.useDeveloperDate = this.systemConfiguration.DemoMode;
	}
	 
	getDataUpdateInterval() {
		return this.systemConfiguration.DemoMode ?
			this.systemConfiguration.OperatorInterfaceDemoMode.dataUpdateInterval :
			this.systemConfiguration.OperatorInterface.dataUpdateInterval;
	}

	getUserName() {
		return this.userName;
	}
	getUserPassword() {
		return this.userPassword;
	}

	// dDate can be a date string or null
	setDeveloperDate( dDate: string | null ) {
		this.developerDate  = dDate;
	}
	getDeveloperDate( ): string | null {
		return this.developerDate;
	}
	setUseDeveloperDate ( use: boolean ) {
		this.useDeveloperDate = use;
	}
	getUseDeveloperDate ( ) {
		return this.useDeveloperDate;
	}

	// Actually calls user web services to log in.
	setUserName ( userName: string, userPassword: string ): Observable<any> {
		// console.log(userName);
		if ( userName === '' || userName == null ) {
			return of(false);
		}
		this.userName = userName;
		this.userPassword = userPassword;

		// use the preferences found in the configuration file
		// this.applyConfigFilePreferences();

		// console.log(userName, this.userPreferences);
		if ( userName === 'demo' || userName === 'encoded' || userName === 'guest' ) {
			this.realUser = false;
			console.log("CALLING setupUser!");
			// use the preferences found in the configuration file
			this.applyConfigFilePreferences();
			this.setupUser();
			return of(true);
		}

		this.realUser = true;
		const usersURL = this.appConfigService.getUsersURL() + 'auth/login';

		// create header for http post
		const userJSON = {'username': userName, 'passwd': userPassword };

		const httpOptions = {
			headers: new HttpHeaders({ 'Content-Type': 'application/json' })};

		console.log('Calling http.post to ' + usersURL + ' to verify ' + userName);

		return this.http.post(usersURL, userJSON, httpOptions );
	}

	// Set user preferences if they exist in user web services.
	setupUser() {
		console.log('In setupUser ' + this.userName + ', real user: ' + this.realUser);
		if (this.realUser) {
			this.usersService.getUser(this.userName).subscribe ( user => {
				this.currentUser = user;
				console.log(this.currentUser);
			});
			this.usersService.getUserPreferences(this.userName).subscribe ( userPreferences => {
				console.log(userPreferences);
				this.applySpecificPreferences(
					userPreferences?.preferences?.preferences);
				this.setupUserPreferences();
			});
		} else {
			this.setupUserPreferences();
		}
	}

	// Helper function to setupUser - now preferences have been set, if available.
	// This function primarily passes preferences onto mapService
	setupUserPreferences() {
		// preferences have been set, whether it be a guest
		// login or a specific user.  Now adjust the settings
		// in the map, dashboards, etc.
		// console.log("Saving userPreferences to observable:", this.userPreferences );
		console.log("In setupUserPreferences");
		this.preferencesBehaviorSubject.next( 
			this.userPreferences );

		this.appReference.initializeBasedOnLogin();
	}
	
	setIncludeDecoration ( includeDecoration: boolean ) {
		this.includeDecorationSubject.next(includeDecoration);
	}

	// If config=<filename.json> was specified in the url, 
	// return true if user is logged in or force login
	// May need to enhance to include whether the logged in user has
	// permission to go to url as well like above.
	readAlternateConfigFile(filename) {
		this.appConfigService.readSecondaryConfigurationFile(filename).then( data => {
			// console.log("Got data from new config", data);
			if ( data != null ) {
				//
				// If logged in or configuration file doesn't require login, process alternate config file
				if (!data.hasOwnProperty("LoginRequired") ||
					(data.hasOwnProperty("LoginRequired") && data.loginRequired == false ) || 
					this.authService.isLoggedIn ) {

					this.processAlternateConfigFile();
					return;
				}

				const username = this._cookieService.get('NOIUserName');
				const password = this._cookieService.get('NOIUserPassword');

				//
				// Not logged in but have cookies.
				// Login with cookies.
				//     If successful, read user configuration and go to /map.
				//     Otherwise, send user to login page.
				//
				if ( username !== 'undefined' ) {
					this.setUserName( username, password )
					.subscribe((data) => {
						console.log(data);
						if ( data.status === 'success' ) {
							this.authService.loginSuccessful ( username, password );
							this.authService.saveAuthorization ( data.Authorization );
							console.log('cookie detected.  user: ' + this._cookieService.get('NOIUserName'));
							console.log('authService.isLoggedIn set to: ' + this.authService.isLoggedIn);
							this.processAlternateConfigFile();
							this.setupUser();
						}  else {
							this.authService.setRedirect( this.location.path(false));
							console.log('Logging in with cookies unsuccessful.  Navigating to login but saving redirect: ', this.location.path());
							this.router.navigate(['/login']);
						}
					});
				} else {
					// navigate to the login page
					console.log('Navigating to login but saving redirect: ', this.location.path());
					this.authService.setRedirect( this.location.path(false));
					this.router.navigate(['/login']);
					return false;
				}
				return true;
			}
		}) ;
	}
	// helper to readAlternateConfigFile
	// actually does the work
	processAlternateConfigFile() {
		this.appConfigService.initializeSystem(this.appConfigService.getSecondaryConfig());
		this.readSystemConfiguration();
		this.applyConfigFilePreferences();
		this.appReference.createOverlayLayersDefinitions ();
		this.appReference.processAdditionalDataDisplayLayers();
	}

	getUserProfile (key: any) {
		return this.userProfile[key];
	}
	setUserProfile (key: any, value: any) {
		let schema = this.userProfile;  // a moving reference to internal objects within userPreferences
		const pList = key.split('.');
		const len = pList.length;
		for (let i = 0; i < len - 1; i++) {
			const elem = pList[i];
			if ( !schema[elem] ) { schema[elem] = {}; }
			schema = schema[elem];
		}

		schema[pList[len - 1]] = value;
	}

	getUserPreference (key: any) {
		return this.userPreferences[key];
	}

	// Only sets the in-memory user preference.
	// WriteUserPreferences must be called to save to user web services.
	setUserPreference (key: any, value: any) {
		// console.log(key, value);
		// console.log(this.userPreferences);
		let schema = this.userPreferences;  // a moving reference to internal objects within userPreferences
		const pList = key.split('.');
		const len = pList.length;
		for (let i = 0; i < len - 1; i++) {
			const elem = pList[i];
			if ( !schema[elem] ) { schema[elem] = {}; }
			schema = schema[elem];
		}

		schema[pList[len - 1]] = value;

	}

	// If the user is a real user (not a guest of demo user),
	// save the user's preferences using user web services.
	writeUserPreferences () {
		if (this.realUser) {
			console.log(this.userPreferences);
			this.preferencesBehaviorSubject.next( 
				this.userPreferences );

			this.usersService.setUserPreferences ( {
				'username': this.userName,
				'preferences': this.userPreferences.preferences}).subscribe();
		}
	}

	// helper function to return the number as a 2-digit number, aka 2 -> 02 and 10 -> 10
	makeTwoDigit ( num ) {
		return ( num < 10 ) ? '0' + num : num;
	}

	// helper to createURI
	// adds queryKey to queryString with values from option
	addQueryToString ( queryString, queryKey, options, optionKey ) {
		if ( options.hasOwnProperty(optionKey)) {
			const divider = queryString.length == 0 ? '' : '&';
			queryString += (divider + queryKey + '=' + 
				options[optionKey]);
		}
		return queryString

	}

	// converts duration string to number of days
	convertDurationToDays ( duration ) {
		const digitsRegex = /[0-9]/g;
		const lettersRegex = /[A-Za-z]/g;
		const digits = duration.match(digitsRegex);
		const letters = duration.match(lettersRegex);
		let dayVal;
		switch (letters) {
			case 'minute':
			case 'minutes': dayVal = 1/(24*60); break;
			case 'hour':
			case 'hours': dayVal = 1/24; break;
			case 'day':
			case 'days': dayVal = 1; break;
			case 'week':
			case 'weeks': dayVal = 7; break;
			case 'month':
			case 'months': dayVal = 30.437; break;
			default: dayVal = 1;
		}
		return digits * dayVal;
	}

	// createURI assembles the URI for retrieving data through data web services
	// options include:
	// filename 
	//   stationSummaries.json,
	//   stationSummaries_<StationId>.json,
	//   stationSummaries.geojson,
	// 	 floodImpactSummaries.json,
	//   floodImpactSummaries.geojson
	//   ts - actually calls the ts service from data web services
	// duration - uses today as end date and sets start date as <duration> days back, 7 days for example
	// Sample output:
	// http://localhost:4200/assets/novastar/rest/novastar-data/api/v1/stationSummaries%253FforOperatorStationSummary=true%2526periodEnd=2015-06-13T00%253A00%253A00-06%253A00
	createURI ( options ) {
		// console.log(this.userName);
		console.log(options);
		let additionalQuery = '';
		const filename = options['filename'];

		if ( filename === 'ts' ) {
			// include either locId or stationNumId
			if ( options.hasOwnProperty('locId')) {
				additionalQuery += 'locId=' + options.locId;
			} else if ( options.hasOwnProperty('id')) {
				additionalQuery += 'stationNumId=' + options.id;
			}
		}
		additionalQuery = this.addQueryToString(
			additionalQuery, 'dataType', options, 'dataType');

		additionalQuery = this.addQueryToString(
			additionalQuery, 'dataInterval', options, 'dataInterval');

		additionalQuery = this.addQueryToString(
			additionalQuery, 'format', options, 'format');

		additionalQuery = this.addQueryToString(
			additionalQuery, 'statistic', options, 'statisticType');

		// console.log(options);
		let dateString = '';
		// if duration has been specified, construct period start date string such as 2018-07-24T00:00:00-06:00, today is assumed to be the end date
		// either duration or startDate and endDate can be specified
		if ( this.useDeveloperDate ) {
			dateString = '&periodEnd=' + this.developerDate;
		} else if (options.hasOwnProperty('duration') ) { 
			const periodEndDate = new Date(Date.now());
			// duration is in days, 24 hrs/day, 60 min/hr, 60 sec/min, to milliseconds
			const duration = options['duration'];
			const regex = /[A-Za-z]/g;
			const days = duration.match(regex) ? 
				this.convertDurationToDays(duration) :
				duration;
			const pSD = new Date(periodEndDate.getTime() - 
				(days * 24 * 60 * 60 * 1000 ));
			dateString = '&periodStart=' + this.createDateString( pSD );
		} else if (options.hasOwnProperty('periodStart') && options.hasOwnProperty('periodEnd')) { 
			console.log(options.periodEnd);
			const periodStartDate = new Date(options.periodStart);
			const periodEndDate = new Date(options.periodEnd);
			const periodStartString = this.createDateString(periodStartDate);
			const periodEndString = this.createDateString(periodEndDate);
			dateString = '&periodStart=' + periodStartString + '&periodEnd=' + periodEndString;
		}
		//
		//  static files
		//
		if ( this.systemConfiguration.DemoMode ) {
			console.log ( 'Demo mode is set to true so demo data is being returned.  Otherwise, the url returned would have been ' +
				this.systemConfiguration.WebServices.dataIP + '/' +
				this.systemConfiguration.WebServices.dataRootURI + '/' + this.getWSRequest(filename, '') + dateString
			);
			const periodEndString = 'periodEnd=' + this.systemConfiguration.OperatorInterfaceDemoMode.periodEndDate;
			const path = this.getWSRequest(filename, '');
			const queryString = path.includes('?') ? 
				path + '&' + periodEndString :
				path + '?' + periodEndString;

			switch ( this.userName ) {
				case 'demo':
				case 'guest':
					return this.baseDataDir + ( encodeURIComponent(queryString).replace(/%3D/g, '=').replace(/%/g, '%25'));
				default:
					// must be an actual user - will this be the same because baseDataDir should include http:...
					return this.baseDataDir + ( encodeURIComponent(queryString).replace(/%3D/g, '=').replace(/%/g, '%25'));
			}
		} else {
			const service = this.getWSRequest(filename, additionalQuery );
			return this.systemConfiguration.WebServices.dataIP + '/' +
				this.systemConfiguration.WebServices.dataRootURI + '/' +
				service + dateString;
		}
	}

	// Helper function to createURI
	// Create a date string for data web services
	// date is a date object
	createDateString ( date ) {
		console.log(date);
		let diff = date.getUTCHours() - date.getHours();
		diff = diff < 0 ? diff + 24 : diff;
		const hoursString = this.makeTwoDigit ( date.getHours());
		const minutesString = this.makeTwoDigit ( date.getMinutes());
		const secondsString = this.makeTwoDigit ( date.getSeconds());
		const diffString = 'T' + hoursString + ':' + minutesString + ':' + secondsString + '-' + ( this.makeTwoDigit(diff) ) + ':00'; // T00:00:00-06:00 for example
		const monthString = this.makeTwoDigit ( date.getMonth() + 1 );
		const dayString = this.makeTwoDigit ( date.getDate() );
		const dateString = date.getFullYear() + '-' + monthString + '-' + dayString + diffString;
		// console.log(dateString);
		return dateString;
	}

	// Helper function to createURI
	// Takes the filename and converts to whatever the web service call would be:
	// stationSummaries.geojson returns something like
	// 		"stationSummaries?format=geojson&forOperatorStationSummary=true"
	// If filename start with "stationSummaries_" then something like stationSummaries_100.json is
	// being passed in and the timeseries for an individual station are being requested.
	getWSRequest ( filename, additionalQuery ) {
		if ( filename === 'floodImpactSummaries.geojson' ) {
			return 'floodImpactSummaries?format=geojson';
		} else if ( filename === 'floodImpactSummaries.json' ) {
			return 'floodImpactSummaries';
		} else if ( filename === 'stationSummaries.geojson' ) {
			return 'stationSummaries?forOperatorStationSummary=true&format=geojson';
		} else if ( filename === 'stationSummaries.json' ) {
			return 'stationSummaries?forOperatorStationSummary=true';
		} else if ( filename.startsWith( 'stationSummaries_' )) {
			const stationNumId = filename.replace('stationSummaries_', 'stationNumId=').replace('.json', '');
			return 'stationSummaries?forOperatorStationDashboard=true&' + stationNumId;
		} else if ( filename === 'ts') {
			return 'ts?' + additionalQuery;
		} else if ( filename === 'stationStatistics.geojson') {
			return 'stationStatistics?format=geojson';
		} else return filename;
	}

	// return the overlayLayers definition, depending on whether operating on demo mode
	// returns null if overlayLayers doesn't exist
	getOverlayLayers(): any[] {
		let overlayLayers = [];
		if ( this.systemConfiguration.DemoMode ) {
			if ( this.systemConfiguration.OperatorInterfaceDemoMode.hasOwnProperty('overlayLayers')) {
				overlayLayers = this.systemConfiguration.OperatorInterfaceDemoMode.overlayLayers;
			}
		} else {
			if ( this.systemConfiguration.OperatorInterface.hasOwnProperty('overlayLayers')) {
				overlayLayers = this.systemConfiguration.OperatorInterface.overlayLayers;
			}
		}
		return overlayLayers;
	}

	verifyBrowser () {
		if (( navigator.userAgent.indexOf('Chrome') >= 0 ) ||
			( navigator.userAgent.indexOf('Firefox') >= 0 )) {
				// User is using Chrome or Firefox
				// Celebrate good times!
				console.log('Chrome or Firefox detected.  Good job!');
		} else if ((navigator.userAgent.indexOf('Opera') !== -1 ) ||
				(navigator.userAgent.indexOf('OPR') !== -1 ) ||
				(navigator.userAgent.indexOf('Safari') !== -1 )) {
				// User is using Opera or Safari.  Issue a warning.
				alert('Operator is designed to run on modern browsers such as Chrome (recommended) or Firefox.\n' +
					'  The current browser has not been well tested.  If issues arise, please try running on Chrome.');
		} else {
				// Internet Explorer is difficult to test for.
				// User may be using Internet Explorer.
				// Rue the day!
				alert('Operator is designed to run on modern browsers such as Chrome (recommended) or Firefox.\n' +
					'  Portions of Operator WILL NOT run on Internet Explorer.');
		}
	}

	// Given a property in the configuration file, returns the value of that taking into consideration
	// whether Operator is being run in demo mode or not.
	createFullPath ( property ) {
		let fullPath = '';
		if ( this.systemConfiguration.DemoMode ) {
			if ( this.systemConfiguration.hasOwnProperty('OperatorInterfaceDemoMode') &&
				this.systemConfiguration.OperatorInterfaceDemoMode.hasOwnProperty(property)) {
				fullPath = this.baseDir + this.systemConfiguration.OperatorInterfaceDemoMode[property];
			}
		} else {
			if ( this.systemConfiguration.hasOwnProperty('OperatorInterface') &&
				this.systemConfiguration.OperatorInterface.hasOwnProperty(property)) {
				fullPath = this.baseDir + this.systemConfiguration.OperatorInterface[property];
			}
		}
		return fullPath;
	}

	createRelativePath ( property ) {
		let relativePath = '';
		if ( this.systemConfiguration.DemoMode ) {
				// external data directory contains the date portion in a directory
				const periodEndDate = this.systemConfiguration.OperatorInterfaceDemoMode.periodEndDate;
				const date = periodEndDate.slice(0, periodEndDate.search('T'));

				relativePath = this.systemConfiguration.OperatorInterfaceDemoMode.externalDataDir +
									date + '/' + this.systemConfiguration.OperatorInterfaceDemoMode[property];
		} else {
				relativePath = this.systemConfiguration.OperatorInterface.externalDataDir + this.systemConfiguration.OperatorInterface[property];
		}
		return relativePath;
	}
}
